/**
 * Report Templates Management Page
 *
 * CRUD for report templates (ADMIN/SUPER_ADMIN only)
 * Allows creating, editing, and managing predefined report templates
 */

import { useState, useMemo, useEffect } from 'react'
import {
  FileText,
  Plus,
  Search,
  Trash2,
  Edit,
  Loader2,
  AlertCircle,
  Power,
  PowerOff,
  Eye,
} from 'lucide-react'
import { Button } from '@/presentation/components/ui/Button'
import { Input } from '@/presentation/components/ui/Input'
import { Select } from '@/presentation/components/ui/Select'
import { Badge } from '@/presentation/components/ui/Badge'
import { Card, CardContent } from '@/presentation/components/ui/Card'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/presentation/components/ui/Table'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/presentation/components/ui/Dialog'
import {
  useTemplates,
  useCreateTemplate,
  useUpdateTemplate,
  useDeleteTemplate,
} from '@/application/report-templates'
import { useActiveModalities } from '@/application/modalities'
import { toast } from '@/lib/toast'
import type { ReportTemplate, CreateTemplateData, UpdateTemplateData } from '@/domain/report-template/types'

// =============================================================================
// Constants
// =============================================================================

const STATUS_OPTIONS = [
  { value: '', label: 'Todas' },
  { value: 'active', label: 'Activas' },
  { value: 'inactive', label: 'Inactivas' },
]

const CATEGORY_OPTIONS = [
  { value: '', label: 'Todas las categorías' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Patológico', label: 'Patológico' },
  { value: 'Urgente', label: 'Urgente' },
  { value: 'Otro', label: 'Otro' },
]

const CATEGORY_FORM_OPTIONS = [
  { value: '', label: 'Sin categoría' },
  { value: 'Normal', label: 'Normal' },
  { value: 'Patológico', label: 'Patológico' },
  { value: 'Urgente', label: 'Urgente' },
  { value: 'Otro', label: 'Otro' },
]

// =============================================================================
// Template Form Modal
// =============================================================================

interface TemplateFormModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template?: ReportTemplate | null
  onSuccess?: () => void
}

function TemplateFormModal({
  open,
  onOpenChange,
  template,
  onSuccess,
}: TemplateFormModalProps) {
  const isEditing = !!template
  const { data: modalitiesData } = useActiveModalities()
  const modalities = modalitiesData?.modalities || []

  const modalityOptions = useMemo(() => [
    { value: '', label: 'Todas las modalidades' },
    ...modalities.map((mod) => ({
      value: mod.code,
      label: `${mod.name} (${mod.code})`,
    })),
  ], [modalities])

  const [formData, setFormData] = useState({
    name: '',
    modalityCode: '',
    category: '',
    findingsTemplate: '',
    conclusionsTemplate: '',
    technicalNotesTemplate: '',
    displayOrder: '',
  })
  const [error, setError] = useState<string | null>(null)

  const createMutation = useCreateTemplate()
  const updateMutation = useUpdateTemplate()

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)

    if (!formData.name.trim()) {
      setError('El nombre es requerido')
      return
    }

    if (!formData.findingsTemplate.trim()) {
      setError('El contenido de hallazgos es requerido')
      return
    }

    try {
      if (isEditing && template) {
        const updateData: UpdateTemplateData = {
          name: formData.name.trim(),
          modalityCode: formData.modalityCode || null,
          category: formData.category || null,
          findingsTemplate: formData.findingsTemplate.trim(),
          conclusionsTemplate: formData.conclusionsTemplate.trim() || null,
          technicalNotesTemplate: formData.technicalNotesTemplate.trim() || null,
          displayOrder: formData.displayOrder ? parseInt(formData.displayOrder, 10) : undefined,
        }
        await updateMutation.mutateAsync({ id: template.id, data: updateData })
        toast.success('Plantilla actualizada correctamente')
      } else {
        const createData: CreateTemplateData = {
          name: formData.name.trim(),
          modalityCode: formData.modalityCode || null,
          category: formData.category || null,
          findingsTemplate: formData.findingsTemplate.trim(),
          conclusionsTemplate: formData.conclusionsTemplate.trim() || null,
          technicalNotesTemplate: formData.technicalNotesTemplate.trim() || null,
          displayOrder: formData.displayOrder ? parseInt(formData.displayOrder, 10) : undefined,
        }
        await createMutation.mutateAsync(createData)
        toast.success('Plantilla creada correctamente')
      }

      handleClose()
      onSuccess?.()
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Error al guardar la plantilla'
      setError(message)
      toast.error(message)
    }
  }

  const handleClose = () => {
    setFormData({
      name: '',
      modalityCode: '',
      category: '',
      findingsTemplate: '',
      conclusionsTemplate: '',
      technicalNotesTemplate: '',
      displayOrder: '',
    })
    setError(null)
    onOpenChange(false)
  }

  // Reset form when template changes or modal opens
  useEffect(() => {
    if (open && template) {
      setFormData({
        name: template.name,
        modalityCode: template.modalityCode || '',
        category: template.category || '',
        findingsTemplate: template.findingsTemplate,
        conclusionsTemplate: template.conclusionsTemplate || '',
        technicalNotesTemplate: template.technicalNotesTemplate || '',
        displayOrder: template.displayOrder?.toString() || '',
      })
    } else if (open && !template) {
      setFormData({
        name: '',
        modalityCode: '',
        category: '',
        findingsTemplate: '',
        conclusionsTemplate: '',
        technicalNotesTemplate: '',
        displayOrder: '',
      })
    }
  }, [open, template])

  const isPending = createMutation.isPending || updateMutation.isPending

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Editar Plantilla' : 'Nueva Plantilla'}
          </DialogTitle>
          <DialogDescription>
            {isEditing
              ? 'Modifique los datos de la plantilla'
              : 'Complete los datos para crear una nueva plantilla de informe'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 bg-red-50 dark:bg-red-950/40 border border-red-200 dark:border-red-800 rounded-lg text-sm text-red-600 dark:text-red-400 flex items-center gap-2">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <Input
              label="Nombre"
              value={formData.name}
              onChange={(e) => handleChange('name', e.target.value)}
              placeholder="Ej: Tórax Normal"
              required
            />
            <Input
              label="Orden"
              type="number"
              value={formData.displayOrder}
              onChange={(e) => handleChange('displayOrder', e.target.value)}
              placeholder="0"
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <Select
              label="Modalidad"
              value={formData.modalityCode}
              onChange={(e) => handleChange('modalityCode', e.target.value)}
              options={modalityOptions}
            />
            <Select
              label="Categoría"
              value={formData.category}
              onChange={(e) => handleChange('category', e.target.value)}
              options={CATEGORY_FORM_OPTIONS}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
              Hallazgos <span className="text-red-500">*</span>
            </label>
            <textarea
              value={formData.findingsTemplate}
              onChange={(e) => handleChange('findingsTemplate', e.target.value)}
              placeholder="Describa la plantilla de hallazgos..."
              rows={5}
              className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--input)] rounded-lg text-sm focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--ring)] resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
              Conclusiones
            </label>
            <textarea
              value={formData.conclusionsTemplate}
              onChange={(e) => handleChange('conclusionsTemplate', e.target.value)}
              placeholder="Plantilla de conclusiones (opcional)..."
              rows={3}
              className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--input)] rounded-lg text-sm focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--ring)] resize-none"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[var(--foreground)] mb-1.5">
              Notas Técnicas
            </label>
            <textarea
              value={formData.technicalNotesTemplate}
              onChange={(e) => handleChange('technicalNotesTemplate', e.target.value)}
              placeholder="Plantilla de notas técnicas (opcional)..."
              rows={2}
              className="w-full px-3 py-2 border border-[var(--border)] bg-[var(--input)] rounded-lg text-sm focus:ring-2 focus:ring-[var(--ring)] focus:border-[var(--ring)] resize-none"
            />
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={handleClose}
              disabled={isPending}
            >
              Cancelar
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
              {isEditing ? 'Guardar Cambios' : 'Crear Plantilla'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Preview Modal
// =============================================================================

interface PreviewModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: ReportTemplate | null
}

function PreviewModal({ open, onOpenChange, template }: PreviewModalProps) {
  if (!template) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Vista Previa: {template.name}</DialogTitle>
          <DialogDescription>
            {template.modalityCode && <Badge variant="secondary" className="mr-2">{template.modalityCode}</Badge>}
            {template.category && <Badge variant="outline">{template.category}</Badge>}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {template.technicalNotesTemplate && (
            <div>
              <h4 className="text-sm font-medium text-[var(--foreground-muted)] mb-1">Notas Técnicas</h4>
              <div className="p-3 bg-[var(--secondary)] rounded-lg text-sm whitespace-pre-wrap">
                {template.technicalNotesTemplate}
              </div>
            </div>
          )}

          <div>
            <h4 className="text-sm font-medium text-[var(--foreground-muted)] mb-1">Hallazgos</h4>
            <div className="p-3 bg-[var(--secondary)] rounded-lg text-sm whitespace-pre-wrap">
              {template.findingsTemplate}
            </div>
          </div>

          {template.conclusionsTemplate && (
            <div>
              <h4 className="text-sm font-medium text-[var(--foreground-muted)] mb-1">Conclusiones</h4>
              <div className="p-3 bg-[var(--secondary)] rounded-lg text-sm whitespace-pre-wrap">
                {template.conclusionsTemplate}
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Delete Confirmation Modal
// =============================================================================

interface DeleteModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  template: ReportTemplate | null
  onConfirm: () => void
  isPending: boolean
}

function DeleteModal({ open, onOpenChange, template, onConfirm, isPending }: DeleteModalProps) {
  if (!template) return null

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Eliminar Plantilla</DialogTitle>
          <DialogDescription>
            ¿Está seguro que desea eliminar la plantilla "{template.name}"? Esta acción no se puede deshacer.
          </DialogDescription>
        </DialogHeader>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isPending}>
            Cancelar
          </Button>
          <Button variant="destructive" onClick={onConfirm} disabled={isPending}>
            {isPending && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            Eliminar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// =============================================================================
// Main Component
// =============================================================================

export function TemplatesPage() {
  // Filters
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [modalityFilter, setModalityFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')

  // Modals
  const [formModalOpen, setFormModalOpen] = useState(false)
  const [previewModalOpen, setPreviewModalOpen] = useState(false)
  const [deleteModalOpen, setDeleteModalOpen] = useState(false)
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null)

  // Data
  const { data: modalitiesData } = useActiveModalities()
  const modalities = modalitiesData?.modalities || []

  const modalityFilterOptions = useMemo(() => [
    { value: '', label: 'Modalidad' },
    ...modalities.map((mod) => ({
      value: mod.code,
      label: mod.code,
    })),
  ], [modalities])

  const { data, isLoading, isError, error, refetch } = useTemplates({
    search: searchTerm || undefined,
    isActive: statusFilter === 'active' ? true : statusFilter === 'inactive' ? false : undefined,
    modalityCode: modalityFilter || undefined,
    category: categoryFilter || undefined,
  })

  const templates = data?.templates || []

  const deleteMutation = useDeleteTemplate()
  const updateMutation = useUpdateTemplate()

  const handleCreate = () => {
    setSelectedTemplate(null)
    setFormModalOpen(true)
  }

  const handleEdit = (template: ReportTemplate) => {
    setSelectedTemplate(template)
    setFormModalOpen(true)
  }

  const handlePreview = (template: ReportTemplate) => {
    setSelectedTemplate(template)
    setPreviewModalOpen(true)
  }

  const handleDeleteClick = (template: ReportTemplate) => {
    setSelectedTemplate(template)
    setDeleteModalOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!selectedTemplate) return
    try {
      await deleteMutation.mutateAsync(selectedTemplate.id)
      toast.success('Plantilla eliminada correctamente')
      setDeleteModalOpen(false)
      setSelectedTemplate(null)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al eliminar la plantilla')
    }
  }

  const handleToggleActive = async (template: ReportTemplate) => {
    try {
      await updateMutation.mutateAsync({
        id: template.id,
        data: { isActive: !template.isActive },
      })
      toast.success(template.isActive ? 'Plantilla desactivada' : 'Plantilla activada')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Error al cambiar estado de la plantilla')
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[var(--foreground)] flex items-center gap-2">
            <FileText className="h-6 w-6" />
            Plantillas de Informe
          </h1>
          <p className="text-sm text-[var(--foreground-muted)] mt-1">
            Gestione las plantillas predefinidas para informes radiológicos
          </p>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Nueva Plantilla
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="py-4">
          <div className="flex flex-wrap gap-4">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-[var(--foreground-muted)]" />
                <Input
                  placeholder="Buscar plantillas..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            <div className="w-40">
              <Select
                value={modalityFilter}
                onChange={(e) => setModalityFilter(e.target.value)}
                options={modalityFilterOptions}
              />
            </div>
            <div className="w-40">
              <Select
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
                options={CATEGORY_OPTIONS}
              />
            </div>
            <div className="w-32">
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                options={STATUS_OPTIONS}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--foreground-muted)]" />
            </div>
          ) : isError ? (
            <div className="flex items-center justify-center py-12 text-[var(--destructive)]">
              <AlertCircle className="h-6 w-6 mr-2" />
              {error?.message || 'Error al cargar plantillas'}
            </div>
          ) : templates.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-[var(--foreground-muted)]">
              <FileText className="h-12 w-12 mb-4 opacity-30" />
              <p>No se encontraron plantillas</p>
              <Button variant="outline" className="mt-4" onClick={handleCreate}>
                <Plus className="h-4 w-4 mr-2" />
                Crear primera plantilla
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nombre</TableHead>
                  <TableHead>Modalidad</TableHead>
                  <TableHead>Categoría</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead className="text-right">Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {templates.map((template) => (
                  <TableRow key={template.id}>
                    <TableCell className="font-medium">{template.name}</TableCell>
                    <TableCell>
                      {template.modalityCode ? (
                        <Badge variant="secondary">{template.modalityCode}</Badge>
                      ) : (
                        <span className="text-[var(--foreground-muted)] text-sm">Todas</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {template.category ? (
                        <Badge variant="outline">{template.category}</Badge>
                      ) : (
                        <span className="text-[var(--foreground-muted)] text-sm">—</span>
                      )}
                    </TableCell>
                    <TableCell>
                      {template.isActive ? (
                        <Badge variant="success">Activa</Badge>
                      ) : (
                        <Badge variant="secondary">Inactiva</Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handlePreview(template)}
                          title="Vista previa"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(template)}
                          title="Editar"
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleToggleActive(template)}
                          title={template.isActive ? 'Desactivar' : 'Activar'}
                        >
                          {template.isActive ? (
                            <PowerOff className="h-4 w-4 text-orange-500" />
                          ) : (
                            <Power className="h-4 w-4 text-green-500" />
                          )}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteClick(template)}
                          title="Eliminar"
                        >
                          <Trash2 className="h-4 w-4 text-[var(--destructive)]" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Modals */}
      <TemplateFormModal
        open={formModalOpen}
        onOpenChange={setFormModalOpen}
        template={selectedTemplate}
        onSuccess={() => refetch()}
      />

      <PreviewModal
        open={previewModalOpen}
        onOpenChange={setPreviewModalOpen}
        template={selectedTemplate}
      />

      <DeleteModal
        open={deleteModalOpen}
        onOpenChange={setDeleteModalOpen}
        template={selectedTemplate}
        onConfirm={handleDeleteConfirm}
        isPending={deleteMutation.isPending}
      />
    </div>
  )
}
